import * as React from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect } from 'react';
import Images from '../../../Utils/images';
import isoWeek from 'dayjs/plugin/isoWeek';
import { styled } from '@mui/material/styles';
import isBetweenPlugin from 'dayjs/plugin/isBetween';
import isSameOrBeforePlugin from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfterPlugin from 'dayjs/plugin/isSameOrAfter';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ClickAwayListener, IconButton } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { getServiceDays, getServiceDateRange } from '../../../Utils/helperFns';

dayjs.extend(isoWeek); // Extend Day.js with the isoWeek plugin
dayjs.extend(isBetweenPlugin);
dayjs.extend(isSameOrBeforePlugin);
dayjs.extend(isSameOrAfterPlugin);

interface CustomPickerDayProps extends PickersDayProps<Dayjs> {
  isSelected: boolean;
  isHovered: boolean;
  isInRange: boolean;
  isInShiftWeek: boolean;
}

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: prop =>
    prop !== 'isSelected' &&
    prop !== 'isHovered' &&
    prop !== 'isInRange' &&
    prop !== 'isInShiftWeek',
})<CustomPickerDayProps>(
  ({ theme, isSelected, isHovered, isInRange, isInShiftWeek, day }) => ({
    borderRadius: 0,
    ...(isSelected && {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      '&:hover, &:focus': {
        backgroundColor: theme.palette.primary.light,
      },
    }),
    ...(isHovered && {
      backgroundColor: 'var(--secondary_blue_50)',
      color: 'var(--secondary_blue_500)',
      '&:hover, &:focus': {
        backgroundColor: 'var(--secondary_blue_50)',
        color: 'var(--secondary_blue_500)',
      },
      ...theme.applyStyles('dark', {
        backgroundColor: 'var(--secondary_blue_500)',
        color: 'var(--white)',
        '&:hover, &:focus': {
          backgroundColor: 'var(--secondary_blue_500)',
          color: 'var(--white)',
        },
      }),
    }),
    ...(isInShiftWeek && {
      backgroundColor: 'var(--secondary_blue_100)',
      color: 'var(--secondary_blue_700)',
      '&:hover, &:focus': {
        backgroundColor: 'var(--secondary_blue_200)',
      },
    }),
    ...(isInRange && {
      backgroundColor: 'var(--secondary_blue_300)',
      color: 'var(--secondary_blue_900)',
      '&:hover, &:focus': {
        backgroundColor: 'var(--secondary_blue_400)',
      },
    }),
    // Week range styling
    ...(isInShiftWeek &&
      day.day() === 0 && {
        borderTopLeftRadius: '50%',
        borderBottomLeftRadius: '50%',
      }),
    ...(isInShiftWeek &&
      day.day() === 6 && {
        borderTopRightRadius: '50%',
        borderBottomRightRadius: '50%',
      }),
  }),
) as React.ComponentType<CustomPickerDayProps>;

const isInServiceRange = (day: Dayjs, startDate: Dayjs, endDate: Dayjs) => {
  return day.isBetween(startDate, endDate, 'day', '[]');
};

const isInSameWeek = (dayA: Dayjs, dayB: Dayjs | null | undefined) => {
  if (dayB == null) {
    return false;
  }
  return dayA.isSame(dayB, 'week');
};

const isWeekContainsShiftDates = (
  weekStart: Dayjs,
  shiftStart: Dayjs,
  shiftEnd: Dayjs,
) => {
  const weekEnd = weekStart.endOf('week');

  // Check if the week overlaps with shift period
  return (
    weekStart.isSameOrBefore(shiftEnd, 'day') &&
    weekEnd.isSameOrAfter(shiftStart, 'day')
  );
};

function Day(
  props: PickersDayProps<Dayjs> & {
    selectedDayStart?: Dayjs | null;
    selectedDayEnd?: Dayjs | null;
    hoveredDay?: Dayjs | null;
    startDate?: Dayjs;
    endDate?: Dayjs;
  },
) {
  const {
    day,
    selectedDayStart,
    selectedDayEnd,
    hoveredDay,
    startDate,
    endDate,
    onSelect: _onSelect,
    ...other
  } = props;

  const isInRange =
    startDate && endDate ? isInServiceRange(day, startDate, endDate) : false;

  const isInShiftWeek =
    startDate && endDate
      ? isWeekContainsShiftDates(day.startOf('week'), startDate, endDate)
      : false;

  const isDisabled = !isInRange;

  return (
    <CustomPickersDay
      {...other}
      day={day}
      sx={{ px: 2.5 }}
      disableMargin
      disabled={isDisabled}
      selected={
        selectedDayStart || selectedDayEnd
          ? day.isSame(selectedDayStart, 'day') ||
            day.isSame(selectedDayEnd, 'day') ||
            day.isBetween(selectedDayEnd, selectedDayStart, 'day')
          : false
      }
      isSelected={
        isInSameWeek(day, selectedDayStart) || isInSameWeek(day, selectedDayEnd)
      }
      isHovered={isInSameWeek(day, hoveredDay)}
      isInRange={isInRange}
      isInShiftWeek={isInShiftWeek}
    />
  );
}

export default function ShiftDateHeader({
  cartData,
  handleDateClick,
  selectedDate,
}: {
  cartData: any;
  handleDateClick: (date: any) => void;
  selectedDate: any;
}) {
  const [isCalendarOpen, setCalendarOpen] = React.useState(false);
  const [hoveredDay, setHoveredDay] = React.useState<Dayjs | null>(null);

  const serviceRange = getServiceDateRange(
    cartData?.startTime,
    cartData?.endTime,
  );

  const [value, setValue] = React.useState<Dayjs | null>(null);
  const scrollRef = React.useRef<HTMLUListElement>(null);

  const [serviceDays, setServiceDays] = React.useState(
    getServiceDays(cartData?.startTime, cartData?.endTime),
  );
  // getServiceDays(cartData?.startTime, cartData?.endTime, value),

  const toggleCalendar = () => {
    setCalendarOpen(prev => !prev);
  };

  const handleClick = (date: Dayjs, isDisabled: boolean) => {
    if (isDisabled) return; // Don't allow clicking disabled dates

    // console.log('Header click - Selected date:', date.format('YYYY-MM-DD'));
    // console.log(
    //   'Header click - Current serviceDays dates:',
    //   serviceDays.map(d => d.date),
    // );

    handleDateClick(date);

    // IMPORTANT: Always recalculate service days based on the clicked date
    // Don't rely on the current serviceDays state which might be from a different chunk
    const newServiceDays = getServiceDays(
      cartData?.startTime,
      cartData?.endTime,
      date,
    );
    setServiceDays(newServiceDays);

    // Fetch shift details for the selected date
    if (serviceRange.start && serviceRange.end) {
      // getViewShiftDetails(cartData, {
      //   from: date.startOf('day'),
      //   to: date.endOf('day')
      // });
    }
  };

  useEffect(() => {
    const newServiceRange = getServiceDateRange(
      cartData?.startTime,
      cartData?.endTime,
    );

    const newServiceDays = getServiceDays(
      cartData?.startTime,
      cartData?.endTime,
      selectedDate || newServiceRange.start,
    );
    setServiceDays(newServiceDays);
  }, [cartData]);
  useEffect(() => {
    if (!selectedDate || !serviceDays.length || !scrollRef.current) return;

    const index = serviceDays.findIndex(day =>
      day.dayjs.isSame(selectedDate?.start, 'day'),
    );

    if (index === -1) return;

    const listItems = scrollRef.current.querySelectorAll<HTMLLIElement>('li');
    const targetEl = listItems[index];

    if (targetEl) {
      targetEl.scrollIntoView({
        behavior: 'smooth',
        inline: 'center', // centers horizontally
        block: 'nearest', // keeps vertical stable
      });
    }
  }, [selectedDate, serviceDays]);

  // console.log('CartDDD =>', cartData);

  // console.log('serviceDays =>', serviceDays);

  return (
    <ClickAwayListener
      onClickAway={() => isCalendarOpen && setCalendarOpen(false)}
    >
      <div className="scheduleCalendar">
        <div className="selectedDates mt-16 mb-4">
          <p className="MuiTypography-h6 fw600">
            {selectedDate ? (
              <>
                {selectedDate?.start?.isSame(selectedDate?.end, 'day') ? (
                  selectedDate?.start?.format('MMM DD, YYYY')
                ) : (
                  <>
                    {selectedDate?.start?.format('MMM DD, YYYY')} –{' '}
                    {selectedDate?.end?.format('MMM DD, YYYY')}
                  </>
                )}
              </>
            ) : (
              <>
                {serviceRange.start?.format('MMM DD, YYYY')} –{' '}
                {serviceRange.end?.format('MMM DD, YYYY')}
              </>
            )}
          </p>
        </div>
        <div className="calendar">
          <div className="icon cursor">
            <IconButton onClick={toggleCalendar}>
              <img src={Images.CalendarIcon} alt="Date" />
            </IconButton>
            {isCalendarOpen && (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <div
                  style={{
                    position: 'absolute',
                    zIndex: 1000,
                    backgroundColor: 'white',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                    borderRadius: '8px',
                    marginTop: '10px',
                  }}
                >
                  <DateCalendar
                    value={selectedDate?.end || value}
                    onChange={(newValue, selectionState) => {
                      if (!newValue) return;

                      // Only commit when a date is selected (not year/month change)
                      if (selectionState === 'finish') {
                        if (
                          serviceRange.start &&
                          serviceRange.end &&
                          isInServiceRange(
                            newValue,
                            serviceRange.start,
                            serviceRange.end,
                          )
                        ) {
                          handleDateClick(newValue);
                          setValue(newValue);

                          // const newServiceDays = getServiceDays(
                          //   cartData?.startTime,
                          //   cartData?.endTime,
                          // );
                          // setServiceDays(newServiceDays);
                        }
                      }
                    }}
                    showDaysOutsideCurrentMonth
                    slots={{ day: Day }}
                    slotProps={{
                      day: ownerState =>
                        ({
                          selectedDayStart: selectedDate?.start,
                          selectedDayEnd: selectedDate?.end,
                          hoveredDay,
                          startDate: serviceRange.start,
                          endDate: serviceRange.end,
                          onPointerEnter: () => setHoveredDay(ownerState.day),
                          onPointerLeave: () => setHoveredDay(null),
                        } as any),
                    }}
                  />
                </div>
              </LocalizationProvider>
            )}
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              width: '100vw',
              overflowX: 'auto',
            }}
          >
            <ul className="dates" ref={scrollRef}>
              {serviceDays?.map(day => (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
                <li
                  key={day.date}
                  style={{
                    minWidth: 140,
                    maxWidth: 140,
                    cursor: day.isDisabled ? 'not-allowed' : 'pointer',
                    opacity: day.isDisabled ? 0.5 : 1,
                  }}
                  onClick={() =>
                    !day.isDisabled && handleClick(day.dayjs, day.isDisabled)
                  }
                  className={
                    selectedDate?.start &&
                    selectedDate?.end &&
                    (day.dayjs.isBetween(
                      selectedDate.start,
                      selectedDate.end,
                      'day',
                    ) ||
                      day.dayjs.isSame(selectedDate.start, 'day') ||
                      day.dayjs.isSame(selectedDate.end, 'day'))
                      ? 'active'
                      : ''
                  }
                >
                  <p>{day.name}</p>
                  <span>{day.dayjs.format('DD')}</span>
                  <p>{day.dayjs.format('MMM')}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </ClickAwayListener>
  );
}
